import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, Target, X } from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RecommendationSystem } from "@/components/recommendations/RecommendationSystem";

interface VagaOption {
  id: string;
  agencia: string | null;
  cliente: string | null;
  areas: string | null;
  status: string | null;
}

function vagaLabel(v: VagaOption): string {
  const partes = [v.agencia, v.cliente, v.areas].filter(Boolean);
  return partes.join(" · ") || v.id.slice(0, 8);
}

/**
 * Painel "Comparar com uma vaga" na listagem: escolhe uma vaga e mostra os
 * freelancers ranqueados pelo motor de recomendação da IA (duas listas lado a
 * lado — Recomendados e Outras opções — com o score e o breakdown por
 * dimensão). Deixa o usuário ver, dentro da listagem, como as métricas da IA
 * organizam os freelancers para uma vaga específica.
 */
export function FreelancersVagaMatch({ className }: { className?: string }) {
  const [open, setOpen] = useState(false);
  const [selectedVagaId, setSelectedVagaId] = useState<string | null>(null);

  const { data: vagas = [], isLoading } = useQuery({
    queryKey: ["vagas-para-match-listagem"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("vagas")
        .select("id, agencia, cliente, areas, status")
        .neq("status", "reprovado")
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data || []) as VagaOption[];
    },
  });

  const selected = useMemo(
    () => vagas.find((v) => v.id === selectedVagaId) || null,
    [vagas, selectedVagaId],
  );

  return (
    <div className={cn("rounded-lg border bg-card/40 p-3 space-y-3", className)}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 text-sm font-medium">
          <Target className="h-4 w-4 text-purple-600" />
          Comparar com uma vaga
        </div>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              role="combobox"
              aria-expanded={open}
              className="justify-between gap-2 min-w-[260px] max-w-[440px]"
            >
              <span className="truncate text-left">
                {selected ? vagaLabel(selected) : "Selecionar vaga..."}
              </span>
              <ChevronsUpDown className="h-3.5 w-3.5 opacity-50 shrink-0" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[440px] p-0" align="start">
            <Command
              filter={(value, search) =>
                value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
              }
            >
              <CommandInput placeholder="Buscar por agência, cliente ou área..." />
              <CommandList>
                <CommandEmpty>
                  {isLoading ? "Carregando vagas..." : "Nenhuma vaga encontrada."}
                </CommandEmpty>
                {vagas.map((v) => (
                  <CommandItem
                    key={v.id}
                    value={`${vagaLabel(v)} ${v.id}`}
                    onSelect={() => {
                      setSelectedVagaId(v.id);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedVagaId === v.id ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <span className="truncate">{vagaLabel(v)}</span>
                  </CommandItem>
                ))}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {selectedVagaId && (
          <Button
            variant="ghost"
            size="sm"
            className="gap-1 text-muted-foreground"
            onClick={() => setSelectedVagaId(null)}
          >
            <X className="h-3.5 w-3.5" />
            Limpar
          </Button>
        )}
      </div>

      {selectedVagaId && (
        <div className="pt-1">
          <RecommendationSystem vagaId={selectedVagaId} />
        </div>
      )}
    </div>
  );
}
